import * as React from 'react'
import { styled } from 'baseui'
import { useTranslation } from 'react-i18next'
import { lightTheme } from '@/customTheme'
import { ResizeSizeItem } from './types'

const TILE_PX = 72
/** Ratio outlines and brand icons stay within ~24px of visual weight. */
const ICON_MAX = 24

interface ResizeSizeTileProps {
  item?: ResizeSizeItem
  name: string
  /** Drawn in place of the item's icon or ratio outline. */
  icon?: React.ReactNode
  active?: boolean
  onClick?: () => void
}

/** A size as a square tile with its name under it — the sideways-row form of ResizeSizeRow. */
export function ResizeSizeTile({ item, name, icon, active = false, onClick }: ResizeSizeTileProps) {
  const { t } = useTranslation()
  const label = t(item?.name ?? name)
  const BrandIcon = item?.icon
  return (
    <Tile type="button" aria-label={label} aria-pressed={active} onClick={onClick}>
      <Box $active={active}>
        {icon ?? (BrandIcon ? (
          <FittedIcon><BrandIcon /></FittedIcon>
        ) : <RatioOutline style={ratioOutlineSize(item?.size)} />)}
      </Box>
      <Name title={label}>{label}</Name>
    </Tile>
  )
}

/**
 * Brand logos keep their own size (most are 16px), and only the few drawn
 * larger (e.g. 50px Amazon, Shopify) scale down to ICON_MAX. They have no
 * viewBox, so they are scaled as drawn rather than resized.
 */
function FittedIcon({ children }: { children: React.ReactNode }) {
  const ref = React.useRef<HTMLSpanElement>(null)
  const [scale, setScale] = React.useState(1)
  React.useLayoutEffect(() => {
    const icon = ref.current?.firstElementChild
    if (!icon) return
    const { width, height } = icon.getBoundingClientRect()
    const largest = Math.max(width, height)
    setScale(largest > ICON_MAX ? ICON_MAX / largest : 1)
  }, [])
  return <span ref={ref} style={{ display: 'flex', transform: scale === 1 ? undefined : `scale(${scale})` }}>{children}</span>
}

function ratioOutlineSize(size?: { width: number; height: number }): React.CSSProperties {
  if (!size) return { width: ICON_MAX, height: ICON_MAX }
  const landscape = size.width > size.height
  const short = Math.max(8, (Math.min(size.width, size.height) / Math.max(size.width, size.height)) * ICON_MAX)
  return landscape ? { width: ICON_MAX, height: short } : { width: short, height: ICON_MAX }
}

const Tile = styled('button', {
  flexShrink: 0,
  width: `${TILE_PX}px`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
})

const Box = styled('span', ({ $active }: { $active: boolean }) => ({
  width: `${TILE_PX}px`,
  height: `${TILE_PX}px`,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '12px',
  background: lightTheme.colors.grayScale50,
  border: `2px solid ${$active ? lightTheme.colors.primary : 'transparent'}`,
}))

const RatioOutline = styled('span', {
  boxSizing: 'border-box',
  border: `2px solid ${lightTheme.colors.blackGray}`,
  borderRadius: '4px',
})

// An approved exception to untruncated labels: names under tiles end in "…".
const Name = styled('span', {
  width: '100%',
  ...lightTheme.typography.Small11medium,
  color: lightTheme.colors.blackGray,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})
